import React, { useCallback, useState } from "react";
import { message } from "antd";

import { Patient } from "../../types/healthcare/PatientTypes";

import { fetchPatient } from "../../api/healthcare/PatientApi";

import PatientModal from "../../pages/healthcare/patient/PatientModal";
import type { UserUuid } from "../../types/identity";

/**
 * Opens the standalone patient detail modal for a single patient id, anywhere in
 * the app. Mirrors `useReferenceModal`'s local-state pattern (no global store): the
 * hook owns the open modal + its fetched patient and exposes `openPatient(id)` plus
 * `modals` to render once near the surface that triggers the open.
 *
 * `openPatient(id)` fetches the single patient the SAME way `PatientsPage` does for
 * its `?id=` deep-link (`fetchPatient(String(id))` → `{ data }`) and renders the
 * existing `<PatientModal>`. Every path degrades gracefully — a missing id, a not
 * found record, or a failed fetch warns/errors and no-ops rather than throwing.
 */
export function useOpenPatientModal(): {
	openPatient: (patientId: UserUuid) => void;
	modals: React.ReactNode;
} {
	const [patient, setPatient] = useState<Patient | null>(null);
	const [open, setOpen] = useState(false);

	const openPatient = useCallback((patientId: UserUuid) => {
		const run = async () => {
			if (!patientId) {
				message.warning("No patient is linked to this item.");
				return;
			}
			const hide = message.loading("Loading patient…", 0);
			try {
				const { data } = await fetchPatient(String(patientId));
				if (!data) {
					message.warning("The patient record could not be found.");
					return;
				}
				setPatient(data);
				setOpen(true);
			} catch {
				message.error("Failed to load the patient record.");
			} finally {
				hide();
			}
		};
		void run();
	}, []);

	const modals = (
		<>
			{open && patient && (
				<PatientModal
					isOpen={open}
					onClose={() => setOpen(false)}
					patient={patient}
				/>
			)}
		</>
	);

	return { openPatient, modals };
}

export default useOpenPatientModal;
